import {useEffect, useState, useRef} from 'react'
import {
    httpGetInvolvedPartyById,
    httpGetHeadquarterById,
    httpGetSectionById,
    httpGetJudicialPanelMemberById
} from '@/features/measures/services/measures.http.ts'
import {FilterFormValues, FilterFieldNames} from '@hooks/useFilterMeasures.tsx'

export const useGetFiltersLabel = (filters: FilterFormValues) => {
    const [isLoading, setIsLoading] = useState(false)
    const [updatedFilters, setUpdatedFilters] = useState<FilterFormValues>(filters)
    const previousFiltersRef = useRef<FilterFormValues>(filters)
    const timeoutRef = useRef<NodeJS.Timeout>(null)

    const fetchFilterLabel = async (id: string, type: 'counter_part' | 'site' | 'section' | 'panel') => {
        try {
            let response
            switch (type) {
                case 'counter_part':
                    response = await httpGetInvolvedPartyById(id)
                    break
                case 'site':
                    response = await httpGetHeadquarterById(id)
                    break
                case 'section':
                    response = await httpGetSectionById(id)
                    break
                case 'panel':
                    response = await httpGetJudicialPanelMemberById(id)
                    break
            }
            return (
                response.data.name ||
                response.data.keyName
                    .split('_')
                    .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ')
            )
        } catch (error) {
            console.error(`Error fetching ${type} label:`, error)
            return ''
        }
    }

    useEffect(() => {
        // Clear any pending timeout
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
        }

        const updateFilterLabels = async () => {
            // Check if filters have actually changed
            const hasFiltersChanged = Object.entries(filters).some(([key, value]) => {
                const typedKey = key as FilterFieldNames
                const previousValue = previousFiltersRef.current[typedKey]

                if (Array.isArray(value) && Array.isArray(previousValue)) {
                    // Check if arrays are different in length or content
                    if (value.length !== previousValue.length) return true

                    // Check if any item is missing a name or has a different id
                    return value.some((item, index) => {
                        const prevItem = previousValue[index]
                        return !prevItem || item.id !== prevItem.id || (item.id && !item.name)
                    })
                }
                return value !== previousValue
            })

            if (!hasFiltersChanged) {
                return
            }

            // Check if we actually need to fetch any labels
            const hasFiltersToUpdate = Object.entries(filters).some(([_, value]) => {
                if (Array.isArray(value)) {
                    return value.some(item => !item.name && item.id)
                }
                return false
            })

            if (!hasFiltersToUpdate) {
                setUpdatedFilters(filters)
                previousFiltersRef.current = filters
                return
            }

            setIsLoading(true)
            try {
                const filterTypes = [
                    {key: 'counter_part', array: filters.counter_part},
                    {key: 'site', array: filters.site},
                    {key: 'section', array: filters.section},
                    {key: 'panel', array: filters.panel}
                ] as const

                let hasUpdates = false
                const newFilters = {...filters}

                for (const {key, array} of filterTypes) {
                    const itemsToUpdate = array.filter(item => !item.name && item.id)
                    if (itemsToUpdate.length === 0) continue

                    const updatedArray = [...array]
                    for (const item of itemsToUpdate) {
                        const name = await fetchFilterLabel(item.id, key)
                        if (name) {
                            const index = updatedArray.findIndex(i => i.id === item.id)
                            if (index !== -1) {
                                updatedArray[index] = {...item, name}
                                hasUpdates = true
                            }
                        }
                    }

                    if (hasUpdates) {
                        newFilters[key] = updatedArray
                    }
                }

                if (hasUpdates) {
                    setUpdatedFilters(newFilters)
                    previousFiltersRef.current = newFilters
                } else {
                    setUpdatedFilters(filters)
                    previousFiltersRef.current = filters
                }
            } finally {
                setIsLoading(false)
            }
        }

        // Debounce the update
        timeoutRef.current = setTimeout(updateFilterLabels, 300)

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
            }
        }
    }, [filters])

    return {
        isLoading,
        updatedFilters
    }
}
